/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../../widgets/livewidget/nls/StringResources',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-common-components/lib/filters',
	'react',
	'react-dom'
], function(View, StringResources, UI_TOOLKIT, UICommon, React, ReactDOM) {

	const { NumericFilter } = UICommon;
	const { Dialog, Root } = UI_TOOLKIT;
	/**
	 * This class renders non numeric prompt dialog, and handles related events.
	 */
	class NumericPromptDialog extends View {

		/**
		 * prop-types are defined in:
		 * [NumericFilter Component]{@link https://github.ibm.com/BusinessAnalytics/ba-ui-common/blob/master/src/filters/NumericFilter.tsx}
		 *
		 */
		constructor(options) {
			super(options);

			this.dashboard = options.dashboard;
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
			this.name = options.name;
			this.dataSourceId = options.dataSourceId;
			const dataSource = this.dashboard.getFeature('DataSources').getDataSource(this.dataSourceId);
			const userPreferences = this.dashboard.getGlassCoreSvc('.UserProfile').preferences;
			this.locale = userPreferences.productLocale || 'en';
			this.columnId = options.columnId;
			this.mdColumn = dataSource && dataSource.getMetadataColumn(this.columnId);
			this.queryService = this.dashboard.getFeature('QueryService');
			this.minMax = null;
			this.fromValue = options.values && options.values[0] ? options.values[0].value : null;
			this.toValue =  options.values && options.values[1] ? options.values[1].value : null;
			this._onOk = options.onOk;
			this._onCancel = options.onCancel;

			this.props = {
				// dialog properties
				title: this.name,
				actionTitle: StringResources.get('promptTitle'),
				locale: this.locale,
				fromValue: this.fromValue,
				toValue: this.toValue,
				includeNulls: false,
				invertDisabled: true,
				decimalPlaces: 5,
				loading: false,
				showLabels: true,
				readOnly: false,
				allowSearch: false,
				allowSwitch: false,
				allowIncludeNulls: false,
				hasMoreData: false,

				// dialog call backs
				onDone: this._onDone.bind(this),
				onApply: this._onDone.bind(this),
				onClear: this._onClear.bind(this),
				onClose: this._onClose.bind(this),
				onChange: this._onChange.bind(this)
			};
			this.dialogProps = {
				ariaLabel: StringResources.get('promptDialog', { promptType: 'Numeric range' }),
				className: 'numeric-prompts',
				size: 'large',
				width: '400px',
				height: '345px',
				clickaway: false
			};
		}

		open() {
			return this._getColumnMinMaxValues().then(minMax => {
				return this._setProps({
					minimumValue: minMax && minMax[0] ? minMax[0].value : 0,
					maximumValue: minMax && minMax[1] ? minMax[1].value : 100,
					fromValue: this.props.fromValue ? this.props.fromValue : minMax[0].value,
					toValue:  this.props.toValue ? this.props.toValue : minMax[1].value,
					loading: false
				});
			});
		}

		_close() {
			this.minMax = null;
			const containerNode = this._getContainerNode();
			if (containerNode) {
				ReactDOM.unmountComponentAtNode(containerNode);
			}
			this.$el.remove();
			this.$el = null;
			this.closed = true;
			super.remove();
		}

		_getContainerNode() {
			return this.$el && this.$el[0];
		}

		_getColumnMinMaxValues() {
			return this.queryService.getColumnMinMaxValues(this.dataSourceId, this.columnId).then((queryResult) => {
				return this._getMinMaxValuesFromQueryResult(queryResult);
			}).catch((e) => {
				throw(e);
			});
		}

		_getMinMaxValuesFromQueryResult(result) {
			const minimumValue = result.getValue(0, 0);
			const maximumValue = result.getValue(0, 1);
			this.minMax = [minimumValue, maximumValue];
			return this.minMax;
		}

		_getValues() {
			return [this.props.fromValue, this.props.toValue];
		}

		_render() {
			const element = (
				<Dialog
					{...this.dialogProps}
				>
					<Root>
						<NumericFilter {...this.props}/>
					</Root>
				</Dialog>
			);
			ReactDOM.render(element, this._getContainerNode());
		}

		_onChange(payload) {
			this._setProps({
				isDirty: true,
				fromValue: payload.fromValue,
				toValue: payload.toValue
			});
		}

		_onDone() {
			if (!this.closed) {
				this._onOk(this._getValues());
				this._close();
			}
		}

		_onClear() {
			this._setProps({
				fromValue: this.minMax[0].value,
				toValue: this.minMax[1].value,
				isDirty: false
			});
		}

		_onClose() {
			this._onCancel();
			this._close();
		}

		/**
		 * Re-render whenever a property is updated.
		 * @param {Object} partialProp
		 */
		_setProps(partialProps) {
			this.props.loading = false;
			Object.assign(this.props, partialProps);
			this._render();
		}
	}

	return NumericPromptDialog;
});
